import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as fabric from 'fabric';
import { getToothShape, TOOTH_ORDER } from '../../utils/toothShapes';
import { applyWhiteningToPath } from '../../utils/drawingUtils';

/**
 * Properly ungroup a Fabric.js 6 Group onto the canvas.
 * Converts each child's coordinates from group-local to canvas-absolute.
 */
export function ungroupOnCanvas(canvas, group) {
  if (!canvas || !group) return;

  const items = group.getObjects().slice();

  // Capture each item's absolute transform while still inside the group
  const transforms = items.map(item => {
    const matrix = item.calcTransformMatrix();
    return fabric.util.qrDecompose(matrix);
  });

  // Remove the group from canvas
  canvas.remove(group);

  // Add each item back with its absolute (canvas-level) transform
  items.forEach((item, idx) => {
    const t = transforms[idx];
    item.group = undefined;
    item.set({
      left: t.translateX,
      top: t.translateY,
      scaleX: t.scaleX,
      scaleY: t.scaleY,
      angle: t.angle,
      skewX: t.skewX || 0,
      skewY: t.skewY || 0,
    });
    item.selectable = true;
    item.hasControls = true;
    item.setCoords();
    canvas.add(item);
  });

  canvas.discardActiveObject();
  canvas.requestRenderAll();
}

const SmileDesignerCanvas = ({
  fabricCanvas,
  setFabricCanvas,
  selectedPhoto,
  activeMode,
  activeTool,
  referencePoints,
  onReferencePointChange,
  designParams,
  onSaveState,
  placingPoint,
  onPlacingPointDone,
  onUploadPhoto,
  aiLoading,
  aiResult,
  showComparison,
  originalPhotoURL,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const bgImageRef = useRef(null);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = new fabric.Canvas(canvasRef.current, {
      backgroundColor: '#1a1a2e',
      selection: true,
      preserveObjectStacking: true,
    });

    setFabricCanvas(canvas);

    return () => {
      canvas.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle canvas resize
  useEffect(() => {
    if (!fabricCanvas || !containerRef.current) return;

    const resizeCanvas = () => {
      const container = containerRef.current;
      if (!container) return;
      const width = container.clientWidth;
      const height = Math.max(500, window.innerHeight - 260);
      fabricCanvas.setDimensions({ width, height });

      if (bgImageRef.current) {
        fitBackgroundImage(fabricCanvas, bgImageRef.current);
      }
      fabricCanvas.renderAll();
    };

    resizeCanvas();
    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [fabricCanvas]);

  // Load photo as background
  useEffect(() => {
    if (!fabricCanvas || !selectedPhoto) return;

    const url = selectedPhoto.downloadURL;
    if (!url) return;

    fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' }).then((img) => {
      bgImageRef.current = img;
      fitBackgroundImage(fabricCanvas, img);
      fabricCanvas.renderAll();
    }).catch((err) => {
      console.error('Failed to load photo:', err);
    });
  }, [fabricCanvas, selectedPhoto]);

  // Handle drawing mode
  useEffect(() => {
    if (!fabricCanvas) return;

    if (activeMode === 'mockup' && activeTool && ['brush', 'eraser', 'whitening'].includes(activeTool)) {
      fabricCanvas.isDrawingMode = true;
    } else {
      fabricCanvas.isDrawingMode = false;
    }
  }, [fabricCanvas, activeMode, activeTool]);

  // Handle whitening path creation + object modified + double-click ungroup
  useEffect(() => {
    if (!fabricCanvas) return;

    const handlePathCreated = (opt) => {
      if (activeTool === 'whitening' && opt.path) {
        applyWhiteningToPath(opt.path);
        fabricCanvas.renderAll();
      }
      onSaveState();
    };

    const handleObjectModified = () => {
      onSaveState();
    };

    // Double-click on a tooth group → ungroup into individual teeth
    const handleDblClick = (opt) => {
      const target = opt.target;
      if (!target || target.customType !== 'toothGroup') return;
      ungroupOnCanvas(fabricCanvas, target);
      onSaveState();
    };

    fabricCanvas.on('path:created', handlePathCreated);
    fabricCanvas.on('object:modified', handleObjectModified);
    fabricCanvas.on('mouse:dblclick', handleDblClick);

    return () => {
      fabricCanvas.off('path:created', handlePathCreated);
      fabricCanvas.off('object:modified', handleObjectModified);
      fabricCanvas.off('mouse:dblclick', handleDblClick);
    };
  }, [fabricCanvas, activeTool, onSaveState]);

  // Handle reference point placement via click
  useEffect(() => {
    if (!fabricCanvas) return;

    const handleMouseDown = (opt) => {
      if (!placingPoint) return;
      if (activeMode === 'mockup' && fabricCanvas.isDrawingMode) return;

      const pointer = fabricCanvas.getScenePoint(opt.e);
      onReferencePointChange(placingPoint, { x: pointer.x, y: pointer.y });
      onPlacingPointDone();
    };

    fabricCanvas.on('mouse:down', handleMouseDown);
    return () => {
      fabricCanvas.off('mouse:down', handleMouseDown);
    };
  }, [fabricCanvas, placingPoint, activeMode, onReferencePointChange, onPlacingPointDone]);

  // Render reference point markers
  const renderReferencePoints = useCallback(() => {
    if (!fabricCanvas) return;

    // Remove existing reference point objects
    const existing = fabricCanvas.getObjects().filter(o => o.customType === 'referencePoint');
    existing.forEach(o => fabricCanvas.remove(o));

    const pointConfigs = {
      midline: { color: '#3B82F6', label: 'M' },
      leftCommissure: { color: '#10B981', label: 'LC' },
      rightCommissure: { color: '#10B981', label: 'RC' },
      incisalEdge: { color: '#EF4444', label: 'IE' },
    };

    Object.entries(referencePoints).forEach(([key, point]) => {
      if (!point) return;
      const config = pointConfigs[key];

      const circle = new fabric.Circle({
        radius: 8,
        fill: config.color,
        stroke: '#FFFFFF',
        strokeWidth: 2,
        selectable: true,
        hasControls: false,
        hasBorders: false,
        customType: 'referencePoint',
        pointKey: key,
        originX: 'center',
        originY: 'center',
        left: point.x,
        top: point.y,
      });

      const label = new fabric.FabricText(config.label, {
        left: point.x + 12,
        top: point.y - 6,
        fontSize: 11,
        fill: '#FFFFFF',
        fontFamily: 'sans-serif',
        fontWeight: 'bold',
        selectable: false,
        evented: false,
        customType: 'referencePoint',
      });

      // Allow dragging reference points to reposition
      circle.on('moving', function () {
        const newPos = { x: this.left, y: this.top };
        label.set({ left: this.left + 12, top: this.top - 6 });
        onReferencePointChange(key, newPos);
      });

      fabricCanvas.add(circle);
      fabricCanvas.add(label);
    });

    // Draw guide lines
    renderGuideLines();
    fabricCanvas.renderAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricCanvas, referencePoints, onReferencePointChange]);

  // Guide lines (midline and incisal edge)
  const renderGuideLines = useCallback(() => {
    if (!fabricCanvas) return;

    const existing = fabricCanvas.getObjects().filter(o => o.customType === 'guideLine');
    existing.forEach(o => fabricCanvas.remove(o));

    const canvasH = fabricCanvas.getHeight();

    if (referencePoints.midline) {
      const ml = new fabric.Line(
        [referencePoints.midline.x, 0, referencePoints.midline.x, canvasH],
        {
          stroke: '#3B82F6',
          strokeWidth: 1,
          strokeDashArray: [6, 4],
          opacity: 0.5,
          selectable: false,
          evented: false,
          customType: 'guideLine',
        }
      );
      fabricCanvas.add(ml);
    }

    if (referencePoints.incisalEdge) {
      const ie = new fabric.Line(
        [0, referencePoints.incisalEdge.y, fabricCanvas.getWidth(), referencePoints.incisalEdge.y],
        {
          stroke: '#EF4444',
          strokeWidth: 1,
          strokeDashArray: [6, 4],
          opacity: 0.5,
          selectable: false,
          evented: false,
          customType: 'guideLine',
        }
      );
      fabricCanvas.add(ie);
    }
  }, [fabricCanvas, referencePoints]);

  // Re-render reference points when they change
  useEffect(() => {
    renderReferencePoints();
  }, [renderReferencePoints]);

  // Render teeth overlay for overlay mode
  useEffect(() => {
    if (!fabricCanvas || activeMode !== 'overlay') return;
    if (!designParams.showTeeth) return;

    renderTeethOverlay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricCanvas, activeMode, designParams, referencePoints]);

  const renderTeethOverlay = useCallback(() => {
    if (!fabricCanvas) return;

    // Remove existing teeth and tooth groups
    const existing = fabricCanvas.getObjects().filter(o =>
      o.customType === 'tooth' || o.customType === 'toothGroup'
    );
    existing.forEach(o => fabricCanvas.remove(o));

    const canvasW = fabricCanvas.getWidth();
    const canvasH = fabricCanvas.getHeight();

    // Calculate positions from reference points or defaults
    const centerX = referencePoints.midline ? referencePoints.midline.x : canvasW / 2;
    const incisalY = referencePoints.incisalEdge ? referencePoints.incisalEdge.y : canvasH * 0.55;
    const smileWidth = (referencePoints.leftCommissure && referencePoints.rightCommissure)
      ? Math.abs(referencePoints.rightCommissure.x - referencePoints.leftCommissure.x)
      : canvasW * 0.6;

    const centralW = smileWidth * designParams.centralWidth;
    const toothHeight = centralW * 1.2;
    const variant = designParams.toothVariant || 'natural';

    // Collect all tooth paths to group them
    const toothPaths = [];

    // Build teeth from center outward on both sides
    [-1, 1].forEach(side => {
      let xOffset = 0;

      for (let i = 0; i < designParams.teethPerSide && i < TOOTH_ORDER.length; i++) {
        const toothType = TOOTH_ORDER[i];
        const shape = getToothShape(toothType, variant);
        if (!shape) continue;

        // Calculate width based on golden ratios
        let w = centralW;
        if (i === 1) w = centralW * designParams.lateralRatio;
        else if (i === 2) w = centralW * designParams.canineRatio;
        else if (i === 3) w = centralW * 0.35;
        else if (i === 4) w = centralW * 0.32;

        const h = toothHeight * (i === 0 ? 1 : i === 1 ? 0.92 : i === 2 ? 0.96 : 0.82);
        const arcOffset = designParams.smileArcCurvature * i * i * 15;

        // No gap — teeth placed touching each other
        const toothX = centerX + side * (xOffset + w / 2);
        const toothY = incisalY + arcOffset;

        // Create path first, then use Fabric's ACTUAL bounding box for scaling
        // (SVG paths have internal margins — nominal shape.width > actual path width)
        const path = new fabric.Path(shape.path, {
          left: toothX,
          top: toothY,
          originX: 'center',
          originY: 'top',
          fill: designParams.toothColor,
          opacity: designParams.toothOpacity,
          stroke: 'rgba(255,255,255,0.9)',
          strokeWidth: 1.5,
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
          customType: 'tooth',
          toothType: toothType,
          toothVariant: variant,
          side: side === -1 ? 'left' : 'right',
          toothIndex: i,
        });

        // Scale using Fabric's computed dimensions — eliminates gaps
        const scaleX = w / path.width;
        const scaleY = h / path.height;
        path.set({
          scaleX: side === -1 ? -scaleX : scaleX,
          scaleY: scaleY,
        });

        toothPaths.push(path);

        xOffset += w;
      }
    });

    // Group all teeth so they move together by default
    if (toothPaths.length > 0) {
      const group = new fabric.Group(toothPaths, {
        selectable: true,
        hasControls: true,
        customType: 'toothGroup',
        subTargetCheck: true,
      });
      fabricCanvas.add(group);
      fabricCanvas.setActiveObject(group);
    }

    fabricCanvas.renderAll();
  }, [fabricCanvas, referencePoints, designParams]);

  // Handle shape tools (rectangle, ellipse) in mockup mode
  useEffect(() => {
    if (!fabricCanvas) return;
    if (activeMode !== 'mockup' || !['rectangle', 'ellipse'].includes(activeTool)) return;

    let isDrawing = false;
    let startPoint = null;
    let shapeObj = null;

    const onMouseDown = (opt) => {
      if (placingPoint) return;
      isDrawing = true;
      const pointer = fabricCanvas.getScenePoint(opt.e);
      startPoint = pointer;

      if (activeTool === 'rectangle') {
        shapeObj = new fabric.Rect({
          left: pointer.x,
          top: pointer.y,
          width: 0,
          height: 0,
          fill: designParams.shapeFill || 'transparent',
          stroke: designParams.shapeStroke || '#000000',
          strokeWidth: designParams.shapeStrokeWidth || 2,
          selectable: true,
        });
      } else {
        shapeObj = new fabric.Ellipse({
          left: pointer.x,
          top: pointer.y,
          rx: 0,
          ry: 0,
          fill: designParams.shapeFill || 'transparent',
          stroke: designParams.shapeStroke || '#000000',
          strokeWidth: designParams.shapeStrokeWidth || 2,
          selectable: true,
        });
      }
      fabricCanvas.add(shapeObj);
      fabricCanvas.renderAll();
    };

    const onMouseMove = (opt) => {
      if (!isDrawing || !shapeObj || !startPoint) return;
      const pointer = fabricCanvas.getScenePoint(opt.e);
      const w = Math.abs(pointer.x - startPoint.x);
      const h = Math.abs(pointer.y - startPoint.y);
      const left = Math.min(pointer.x, startPoint.x);
      const top = Math.min(pointer.y, startPoint.y);

      if (activeTool === 'rectangle') {
        shapeObj.set({ left, top, width: w, height: h });
      } else {
        shapeObj.set({ left: left + w / 2, top: top + h / 2, rx: w / 2, ry: h / 2, originX: 'center', originY: 'center' });
      }
      fabricCanvas.renderAll();
    };

    const onMouseUp = () => {
      if (isDrawing && shapeObj) {
        fabricCanvas.setActiveObject(shapeObj);
        onSaveState();
      }
      isDrawing = false;
      shapeObj = null;
      startPoint = null;
    };

    fabricCanvas.on('mouse:down', onMouseDown);
    fabricCanvas.on('mouse:move', onMouseMove);
    fabricCanvas.on('mouse:up', onMouseUp);

    return () => {
      fabricCanvas.off('mouse:down', onMouseDown);
      fabricCanvas.off('mouse:move', onMouseMove);
      fabricCanvas.off('mouse:up', onMouseUp);
    };
  }, [fabricCanvas, activeMode, activeTool, placingPoint, designParams, onSaveState]);

  // Handle text tool
  useEffect(() => {
    if (!fabricCanvas) return;
    if (activeMode !== 'mockup' || activeTool !== 'text') return;

    const onMouseDown = (opt) => {
      if (placingPoint) return;
      // Only add text if clicking on empty area (not on an existing object)
      if (opt.target) return;

      const pointer = fabricCanvas.getScenePoint(opt.e);
      const text = new fabric.IText('Text', {
        left: pointer.x,
        top: pointer.y,
        fontSize: designParams.fontSize || 16,
        fill: designParams.fontColor || '#000000',
        fontFamily: 'Arial',
        selectable: true,
        editable: true,
      });
      fabricCanvas.add(text);
      fabricCanvas.setActiveObject(text);
      text.enterEditing();
      onSaveState();
    };

    fabricCanvas.on('mouse:down', onMouseDown);
    return () => {
      fabricCanvas.off('mouse:down', onMouseDown);
    };
  }, [fabricCanvas, activeMode, activeTool, placingPoint, designParams, onSaveState]);

  return (
    <div
      ref={containerRef}
      className="relative flex-1 bg-gray-900 rounded-lg overflow-hidden border border-gray-700"
      style={{ minHeight: 500 }}
    >
      <canvas ref={canvasRef} />
      {placingPoint && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-10">
          Click on the photo to place {placingPoint.replace(/([A-Z])/g, ' $1').toLowerCase()}
        </div>
      )}
      {!selectedPhoto && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
            </svg>
            <p className="text-lg font-medium">No photo loaded</p>
            <p className="text-sm mt-1 mb-4">Select a phase photo from the toolbar, or upload one directly</p>
            <button
              onClick={onUploadPhoto}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              Upload Photo
            </button>
          </div>
        </div>
      )}
      {/* AI Loading Overlay */}
      {aiLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-20 rounded-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent mb-4" />
          <p className="text-white text-sm font-medium">Generating AI smile design...</p>
          <p className="text-gray-300 text-xs mt-1">This may take 10-20 seconds</p>
        </div>
      )}
      {/* Before/After Comparison Slider */}
      {showComparison && aiResult && originalPhotoURL && (
        <BeforeAfterSlider
          beforeURL={originalPhotoURL}
          afterURL={
            aiResult.imageBase64
              ? `data:${aiResult.mimeType};base64,${aiResult.imageBase64}`
              : null
          }
          containerRef={containerRef}
        />
      )}
    </div>
  );
};

// Before/After comparison slider overlay
const BeforeAfterSlider = ({ beforeURL, afterURL, containerRef }) => {
  const [sliderPos, setSliderPos] = useState(50);
  const isDragging = useRef(false);

  const updatePos = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    setSliderPos(Math.max(2, Math.min(98, (x / rect.width) * 100)));
  }, [containerRef]);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    updatePos(e.clientX);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      updatePos(e.clientX);
    };
    const handleMouseUp = () => { isDragging.current = false; };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updatePos]);

  if (!beforeURL || !afterURL) return null;

  return (
    <div
      className="absolute inset-0 z-10 cursor-col-resize select-none"
      onMouseDown={handleMouseDown}
    >
      {/* After image (full) */}
      <img src={afterURL} alt="After" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
      {/* Before image (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
        <img
          src={beforeURL}
          alt="Before"
          className="absolute inset-0 h-full object-contain"
          style={{ width: containerRef.current ? `${containerRef.current.clientWidth}px` : '100%' }}
          draggable={false}
        />
      </div>
      {/* Slider handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
          </svg>
        </div>
      </div>
      {/* Labels */}
      <div className="absolute top-3 left-3 bg-black bg-opacity-60 text-white px-2.5 py-1 rounded text-xs font-medium z-20">Before</div>
      <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white px-2.5 py-1 rounded text-xs font-medium z-20">After</div>
    </div>
  );
};

// Helper: fit image to canvas as background
export function fitBackgroundImage(canvas, img) {
  const canvasW = canvas.getWidth();
  const canvasH = canvas.getHeight();
  const scale = Math.min(canvasW / img.width, canvasH / img.height, 1);

  img.scaleX = scale;
  img.scaleY = scale;
  img.left = (canvasW - img.width * scale) / 2;
  img.top = (canvasH - img.height * scale) / 2;

  canvas.backgroundImage = img;
}

export default SmileDesignerCanvas;
